/**
 * AuthHeader — the headline + subtitle pair every auth form opens with.
 *
 * Five screens hand-rolled this pair with the same type scale, the same
 * two-step entrance (STAGGER, STAGGER * 2) and the same rhythm tokens.
 * Only two things ever varied, so only those are props: the gap that
 * follows the subtitle, and its colour on forgot-password.
 *
 * Breakpoint source is `useBreakpoint()` — the same hook the screens that
 * use this component already read. Screens that derive their own
 * `width >= 900` (phone-login, phone-verify) intentionally do NOT use this
 * component: adopting it would silently change their type scale between
 * 900 and 1024px.
 */
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { FONTS } from '../../lib/brand'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, DURATION, STAGGER } from '../../lib/theme/tokens'

interface AuthHeaderProps {
  title: string
  subtitle: string
  /**
   * Gap under the subtitle. `'form'` (default) precedes input fields;
   * `'action'` precedes a lone CTA, as on the phone-auth-disabled branch.
   */
  subtitleGap?: 'form' | 'action'
  /**
   * Subtitle colour. `'secondary'` is canonical; `'primary'` preserves the
   * louder subtitle forgot-password has always rendered.
   */
  subtitleTone?: 'secondary' | 'primary'
}

export function AuthHeader({
  title,
  subtitle,
  subtitleGap = 'form',
  subtitleTone = 'secondary',
}: AuthHeaderProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const sp = useAuthRhythm()
  const reducedMotion = useReducedMotion()

  const isWide = isDesktop

  return (
    <>
      <Animated.Text
        entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
        accessibilityRole="header"
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.88}
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: isWide ? 28 : 30,
          lineHeight: isWide ? 34 : 36,
          marginTop: sp.headerTop,
          color: colors.textPrimary,
          textAlign: isWide ? 'left' : 'center',
        }}
      >
        {title}
      </Animated.Text>

      <Animated.Text
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
        }
        style={{
          fontFamily: FONTS.body,
          fontSize: 15,
          lineHeight: 22,
          marginTop: sp.headlineToSub,
          marginBottom: subtitleGap === 'action' ? sp.formToAction : sp.subToForm,
          color: subtitleTone === 'primary' ? colors.textPrimary : colors.textSecondary,
          textAlign: isWide ? 'left' : 'center',
        }}
      >
        {subtitle}
      </Animated.Text>
    </>
  )
}
